<rt-select
  [hasInputTrigger]="hasInputTrigger()"
  [options]="options()"
  [disabled]="disabled"
  [readOnly]="readOnly()"
  [searchPlaceholder]="
    searchPlaceholder() || ('app.common.placeholders.search.jobs.other' | translate)
  "
  [triggerTemplate]="triggerTemplate"
  [optionTemplate]="optionTemplate"
  [labelTemplate]="labelTemplate"
  [headerTemplate]="headerTemplate()"
  [value]="value"
  [placeholder]="placeholder()"
  [tabIndex]="tabIndex()"
  [hasSearch]="hasSearch()"
  [searchCaseSensitive]="searchCaseSensitive()"
  [limit]="limit()"
  [noResultsMessage]="noResultsMessage()"
  [includeEmptyOption]="includeEmptyOption()"
  [emptyOptionLabel]="emptyOptionLabel()!"
  [multiple]="multiple()"
  [closeOnSelect]="closeOnSelect()"
  [minDropdownWidth]="minDropdownWidth()"
  [maxDropdownWidth]="maxDropdownWidth()"
  [noMaxHeight]="true"
  [dropdownPositions]="dropdownPositions()"
  [hasDropdownSeparator]="true"
  (valueChange)="handleChange($any($event))"
  (focus)="focus.emit($event)"
  (blur)="blur.emit($event)"
  class="offer-select"
/>

<ng-template
  #triggerTemplate
  let-disabled="disabled"
  let-readOnly="readOnly"
  let-placeholder="placeholder"
  let-selectedOption="selectedOption"
  let-dropdownOpened="dropdownOpened"
  let-toggleDropdown="toggleDropdown"
>
  @if (!hasInputTrigger() && buttonVariant === 'plain') {
    <rt-button
      #trigger
      [disabled]="disabled || readOnly"
      [attr.tabindex]="disabled ? undefined : tabIndex()"
      (click)="!disabled && !readOnly && toggleDropdown(true)"
      variant="plain"
      size="small"
      [dropdownOpened]="dropdownOpened"
    >
      <!-- SECURITY: Right now we allow any html in here... but maybe we'll change it in the future? -->
      @if (!(selectedOption && selectedOption.value && selectedOption.label)) {
        <span [innerHTML]="placeholder" class="select-placeholder"> </span>
      }
      @if (selectedOption && selectedOption.value && selectedOption.label) {
        <span layout-horizontal layout-align-items-center>
          @if (showStatus() && selectedOption._isOffer) {
            <rt-offer-status-indicator
              [offerStatus]="getOfferStatus(selectedOption.value)!"
              margin-right-small-x
            />
          }
          <rt-truncate>{{ selectedOption.label }}</rt-truncate>
        </span>
      }
    </rt-button>
  }

  @if (!hasInputTrigger() && buttonVariant !== 'plain') {
    <rt-button
      #trigger
      [disabled]="disabled || readOnly"
      [attr.tabindex]="disabled ? undefined : tabIndex()"
      (click)="!disabled && !readOnly && toggleDropdown(true)"
      [variant]="buttonVariant"
      [size]="buttonSize()"
      [dropdownOpened]="dropdownOpened"
    >
      <!-- SECURITY: Right now we allow any html in here... but maybe we'll change it in the future? -->
      @if (!(selectedOption && selectedOption.value && selectedOption.label)) {
        <rt-truncate class="truncate">
          <span [innerHTML]="placeholder" class="select-placeholder"> </span>
        </rt-truncate>
      }
      @if (selectedOption && selectedOption.value && selectedOption.label) {
        <span layout-horizontal layout-align-items-center>
          @if (selectedOption?.icon) {
            <rt-icon [name]="selectedOption.icon" />
          }
          @if (showStatus() && selectedOption._isOffer) {
            <rt-offer-status-indicator
              [offerStatus]="getOfferStatus(selectedOption.value)!"
              margin-right-small-x
            />
          }
          @if (!showStageName()) {
            <span overflow-hidden>
              <rt-truncate class="truncate">{{ selectedOption.label }}</rt-truncate>
            </span>
          }
          @if (showStageName()) {
            <span layout-horizontal>
              <rt-truncate>{{ selectedOption.label }}</rt-truncate>
              @if (getStageName(selectedOption.value); as stageName) {
                <span>&nbsp;/ {{ stageName }}</span>
              }
            </span>
          }
        </span>
      }
    </rt-button>
  }

  @if (hasInputTrigger()) {
    <rt-input
      #trigger
      [disabled]="disabled"
      [readOnly]="true"
      [placeholder]="placeholder"
      [tabIndex]="tabIndex()!"
      [hasDropdownIndicator]="true"
      [class.dropdownOpened]="dropdownOpened"
      [value]="
        multiple() ? '' : selectedOption.value && selectedOption.label ? selectedOption.label : ''
      "
      (click)="!disabled && !readOnly && !dropdownOpened && toggleDropdown(true)"
      [variant]="inputVariant()"
    >
      @if (selectedOption?.icon) {
        <rt-icon prefix [name]="selectedOption.icon" />
      }
      @if (showStatus() && selectedOption && selectedOption._isOffer) {
        <rt-offer-status-indicator prefix [offerStatus]="getOfferStatus(selectedOption.value)!" />
      }
    </rt-input>
  }
</ng-template>

<ng-template #optionTemplate let-option="option" let-selectedOption="selectedOption">
  <div layout-horizontal layout-align-items-center layout-space-between class="option">
    <div layout-horizontal layout-align-items-center>
      @if (showStatus() && option._isOffer) {
        <rt-offer-status-indicator
          [offerStatus]="getOfferStatus(option.value)!"
          margin-right-small-x
        />
      }
      <div ngPreserveWhitespaces class="option-inner-container">
        <span
          [attr.text-default]="option.count === 0 || undefined"
          [attr.medium]="option.count !== 0 || undefined"
          >{{ option.label }}
        </span>
        @if (option.count !== undefined && option.count !== null) {
          <span text-default>({{ option.count }}) </span>
        }
        @if (includeOfferLocation()) {
          <div>
            <small text-light>
              @if (option.locationIds?.length) {
                <rt-assigned-locations-info-container [locationIds]="option.locationIds" />
              } @else {
                <span>{{ option.location }}</span>
              }
            </small>
          </div>
        }
      </div>
    </div>
    @if (showCheckmark() && option === selectedOption) {
      <rt-icon name="check" colorToken="colorIconSuccessDefault" />
    }
  </div>
</ng-template>

<ng-template #labelTemplate let-option="option" let-removeValue="removeValue">
  <ng-template #symbolTemplate>
    @if (showStatus() && option._isOffer) {
      <rt-offer-status-indicator
        [offerStatus]="getOfferStatus(option.value)!"
        margin-right-small-x
      />
    }
  </ng-template>

  <rt-tag
    [deletable]="true"
    [name]="option.label"
    [counter]="option.count"
    [wrapText]="true"
    [symbolTpl]="symbolTemplate"
    [isClickable]="false"
    [isHoverable]="false"
    (deleteClick)="removeValue(option.value)"
  />
</ng-template>
